import {
  InputBase,
  Popper,
  ClickAwayListener,
  Tooltip as MuiTooltip,
} from '@mui/material'
import { styled, keyframes } from '@mui/system'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { colorPalette } from '../../style/colorPalette'
import { Checkbox } from '../../components/inputs/Checkbox'
import { SelectOption } from '../../model/selectOption'
import { intl } from '../../../i18n'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import {
  buildTree,
  countTreeNodes,
  filterTree,
  TreeSelectOption,
} from './treeUtils'

// sticky top 計算用の行高さ（padding除去後）
const ROW_HEIGHT = 26

type Props = {
  open: boolean
  anchorEl: Element | undefined
  options: SelectOption[]
  parentMap: Record<string, string>
  isLoading?: boolean
  selectedValues: Set<string>
  onToggleItem: (value: string) => void
  onClose: () => void
  disablePortal?: boolean
  /**
   * 単一選択として使う場合に指定する。複数選択では祖先を選ぶと配下を選択不可に
   * するが、単一選択では選択済みノードの配下へ選び直せる必要があるため抑制しない。
   */
  singleSelect?: boolean
  searchPlaceholder?: string
}

// Popper内の検索欄・フッター等のおおよその高さ + ビューポート端との余白
const POPPER_CHROME_HEIGHT = 100
const POPPER_OFFSET_Y = 20
const OPTIONS_MAX_HEIGHT_DEFAULT = 600
const OPTIONS_MIN_HEIGHT = 200

const ProcessScopeSearchSelectPopper = ({
  open,
  anchorEl,
  options,
  parentMap,
  isLoading,
  selectedValues,
  onToggleItem,
  onClose,
  disablePortal = true,
  singleSelect = false,
  searchPlaceholder = intl.formatMessage({
    id: 'wbsItemSearchFilterPanel.processScope.searchPlaceholder',
  }),
}: Props) => {
  const [ready, setReady] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const [optionsMaxHeight, setOptionsMaxHeight] = useState(
    OPTIONS_MAX_HEIGHT_DEFAULT
  )
  const initialAnchorHeightRef = useRef<number | null>(null)

  useEffect(() => {
    if (!open || !anchorEl) return
    const updateMaxHeight = () => {
      const anchorRect = anchorEl.getBoundingClientRect()
      const dialogPaper = anchorEl.closest('.MuiPaper-root')
      const bottomEdge = dialogPaper
        ? dialogPaper.getBoundingClientRect().bottom
        : window.innerHeight
      const available =
        bottomEdge - anchorRect.bottom - POPPER_OFFSET_Y - POPPER_CHROME_HEIGHT
      setOptionsMaxHeight(
        Math.max(
          OPTIONS_MIN_HEIGHT,
          Math.min(OPTIONS_MAX_HEIGHT_DEFAULT, available)
        )
      )
    }
    updateMaxHeight()
    window.addEventListener('resize', updateMaxHeight)
    return () => window.removeEventListener('resize', updateMaxHeight)
  }, [open, anchorEl])

  useEffect(() => {
    const timerId = setTimeout(() => setReady(true), 0)
    return () => {
      clearTimeout(timerId)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  useEffect(() => {
    setExpandedNodes(new Set(options.map(o => o.value)))
  }, [options])

  const handleSearchTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        setFilterText(value)
        setExpandedNodes(new Set(options.map(o => o.value)))
      }, 300)
    },
    [options]
  )

  const toggleExpand = useCallback((e: React.MouseEvent, value: string) => {
    e.stopPropagation()
    setExpandedNodes(prev => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const tree = useMemo(
    () => buildTree(options, parentMap),
    [options, parentMap]
  )

  const filteredTree = useMemo(
    () => filterTree(tree, filterText),
    [tree, filterText]
  )

  const filteredCount = useMemo(
    () => countTreeNodes(filteredTree),
    [filteredTree]
  )

  const totalCount = useMemo(() => countTreeNodes(tree), [tree])

  // ネストしたDOM構造でレンダリングすることで、sticky親要素が
  // 子孫のスクロール完了後に一緒に流れるようにする
  const renderTreeNodes = (
    nodes: TreeSelectOption[],
    depth: number,
    isAncestorSelected: boolean
  ): React.ReactNode =>
    nodes.map(node => {
      const isSelected = selectedValues.has(node.value)
      const isExpanded = expandedNodes.has(node.value)
      return (
        <div key={node.value}>
          <TreeNodeRow
            node={node}
            depth={depth}
            isAncestorSelected={isAncestorSelected}
            isExpanded={isExpanded}
            isSelected={isSelected}
            onToggleItem={onToggleItem}
            onToggleExpand={toggleExpand}
          />
          {node.children.length > 0 &&
            isExpanded &&
            renderTreeNodes(
              node.children,
              depth + 1,
              !singleSelect && (isSelected || isAncestorSelected)
            )}
        </div>
      )
    })

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      sx={{
        zIndex: 1301,
        background: colorPalette.monotone[0],
        boxShadow:
          'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
        borderRadius: '4px',
        padding: '10px',
        minWidth: '445px',
        maxWidth: '700px',
      }}
      placement="bottom-start"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: ({ reference }: { reference: { height: number } }) => {
              if (initialAnchorHeightRef.current === null) {
                initialAnchorHeightRef.current = reference.height
              }
              const heightDiff =
                reference.height - initialAnchorHeightRef.current
              return [0, POPPER_OFFSET_Y - heightDiff]
            },
          },
        },
        {
          // 動的maxHeightで下に収まるようにしているためflip不要
          name: 'flip',
          enabled: false,
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            // 横方向のはみ出し防止のみ。縦方向はmaxHeightで制御
            altAxis: false,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
      disablePortal={disablePortal}
    >
      <ClickAwayListener onClickAway={onClose}>
        <PopperContent>
          <InputBase
            autoFocus={true}
            placeholder={searchPlaceholder}
            onChange={handleSearchTextChange}
            sx={{
              border: `1px solid ${colorPalette.monotone[2]}`,
              borderRadius: '4px',
              padding: '4px 8px',
              marginBottom: '8px',
              fontSize: '14px',
            }}
          />
          <OptionsList style={{ maxHeight: `${optionsMaxHeight}px` }}>
            {!ready || isLoading ? (
              <LoadingContainer>
                <Spinner />
              </LoadingContainer>
            ) : (
              renderTreeNodes(filteredTree, 0, false)
            )}
          </OptionsList>
          <FooterContainer>
            <CountLabel>
              <CountNumerator>
                {filteredCount}
                {intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.processScope.count',
                })}
              </CountNumerator>
              {' / '}
              {totalCount}
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.processScope.count',
              })}
            </CountLabel>
          </FooterContainer>
        </PopperContent>
      </ClickAwayListener>
    </Popper>
  )
}

const PopperContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const OptionsList = styled('div')({
  overflowY: 'auto',
})

const OptionItem = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  background: colorPalette.monotone[0],
  '&:hover': {
    background: colorPalette.monotone[1],
  },
  borderRadius: '4px',
  padding: '2px 0',
})

const OptionItemContent = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
})

const ExpandToggle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  cursor: 'pointer',
  flexShrink: 0,
})

const OptionLabel = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  margin: '2px 10px',
  flex: 1,
  minWidth: 0,
})

const EllipsisSpan = styled('span')({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

type EllipsisTextProps = {
  children: string
  className?: string
}

// ellipsis発生時のみツールチップを表示するテキストコンポーネント
const EllipsisText = memo(({ children, className }: EllipsisTextProps) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)

  const handleMouseEnter = useCallback(() => {
    if (ref.current && ref.current.scrollWidth > ref.current.clientWidth) {
      setOpen(true)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <MuiTooltip
      title={children}
      open={open}
      disableHoverListener={true}
      disableFocusListener={true}
      disableTouchListener={true}
    >
      <EllipsisSpan
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={className}
      >
        {children}
      </EllipsisSpan>
    </MuiTooltip>
  )
})

const OptionName = styled(EllipsisText)({
  fontSize: '12px',
  lineHeight: 1.43,
  color: colorPalette.monotone[4],
})

const OptionPath = styled(EllipsisText)({
  fontSize: '11px',
  lineHeight: '14px',
  color: colorPalette.monotone[3],
})

const ExpandTogglePlaceholder = styled('div')({
  width: '20px',
  height: '20px',
  flexShrink: 0,
})

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px 0',
})

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled('div')({
  width: '28px',
  height: '28px',
  border: `3px solid ${colorPalette.monotone[2]}`,
  borderTop: `3px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: `${spin} 0.8s linear infinite`,
})

const FooterContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginTop: '8px',
})

const CountLabel = styled('span')({
  fontSize: '12px',
  color: colorPalette.monotone[3],
})

const CountNumerator = styled('span')({
  fontWeight: 'bold',
  color: colorPalette.monotone[5],
})

type TreeNodeRowProps = {
  node: TreeSelectOption
  depth: number
  isAncestorSelected: boolean
  isExpanded: boolean
  isSelected: boolean
  onToggleItem: (value: string) => void
  onToggleExpand: (e: React.MouseEvent, value: string) => void
}

const TreeNodeRow = memo(
  ({
    node,
    depth,
    isAncestorSelected,
    isExpanded,
    isSelected,
    onToggleItem,
    onToggleExpand,
  }: TreeNodeRowProps) => {
    const hasChildren = node.children.length > 0
    const stickyTop = depth * ROW_HEIGHT

    return (
      <OptionItem
        onClick={
          isAncestorSelected ? undefined : () => onToggleItem(node.value)
        }
        style={{
          paddingLeft: `${depth * 20 + 2}px`,
          ...(hasChildren && {
            position: 'sticky',
            top: `${stickyTop}px`,
            zIndex: 100 - depth,
            paddingTop: 0,
            paddingBottom: 0,
          }),
          ...(isAncestorSelected && {
            cursor: 'default',
          }),
        }}
      >
        <OptionItemContent
          style={isAncestorSelected ? { opacity: 0.4 } : undefined}
        >
          {hasChildren ? (
            <ExpandToggle onClick={e => onToggleExpand(e, node.value)}>
              {isExpanded ? (
                <ExpandLessRounded
                  sx={{
                    fontSize: 16,
                    color: colorPalette.monotone[4],
                    transform: 'rotate(180deg)',
                  }}
                />
              ) : (
                <ExpandMoreRounded
                  sx={{
                    fontSize: 16,
                    color: colorPalette.monotone[4],
                    transform: 'rotate(-90deg)',
                  }}
                />
              )}
            </ExpandToggle>
          ) : (
            <ExpandTogglePlaceholder />
          )}
          <Checkbox
            sx={{ marginRight: '8px' }}
            checked={isSelected || isAncestorSelected}
            disabled={isAncestorSelected}
          />
          {node.iconUrl && <img src={node.iconUrl} />}
          <OptionLabel>
            {depth === 0 && node.tooltip && (
              <OptionPath>{node.tooltip}</OptionPath>
            )}
            <OptionName>{node.name}</OptionName>
          </OptionLabel>
        </OptionItemContent>
      </OptionItem>
    )
  }
)

export default ProcessScopeSearchSelectPopper
